/**
 * useAdminManagement
 *
 * Owns the super-admin panel's data + actions: loads the admin list, exposes
 * mutation helpers (password reset, enable/disable, delete) that re-fetch on
 * success, and lazily loads a selected admin's login history.
 *
 * All calls go through services/adminManagementApi (cookie-authenticated HTTP,
 * guarded server-side by requireSuperAdmin).
 */

import { useState, useCallback, useEffect } from 'react';
import type { AdminPermissions, GameType } from '@swtorbit/shared-socket-events';
import {
  listAdmins,
  getLoginHistory,
  createAdmin,
  resetAdminPassword,
  resetAdminUsage,
  setAdminDisabled,
  deleteAdmin,
  type AdminSummary,
  type AdminLoginEvent,
} from '../services/adminManagementApi';
import { logger } from '../utils/logger';

const log = logger.child('AdminMgmt');

export interface UseAdminManagement {
  admins: AdminSummary[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  addAdmin: (
    username: string,
    password: string,
    isSuperAdmin: boolean,
    permissions: AdminPermissions,
  ) => Promise<void>;
  resetPassword: (adminId: number, password: string) => Promise<void>;
  resetUsage: (adminId: number, gameType: GameType) => Promise<void>;
  setDisabled: (adminId: number, disabled: boolean) => Promise<void>;
  removeAdmin: (adminId: number) => Promise<void>;
  loadLoginHistory: (adminId: number) => Promise<AdminLoginEvent[]>;
}

export function useAdminManagement(): UseAdminManagement {
  const [admins, setAdmins] = useState<AdminSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setAdmins(await listAdmins());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load admins';
      log.error('Failed to load admins', { err });
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addAdmin = useCallback(
    async (
      username: string,
      password: string,
      isSuperAdmin: boolean,
      permissions: AdminPermissions,
    ) => {
      await createAdmin(username, password, isSuperAdmin, permissions);
      await refresh();
    },
    [refresh],
  );

  const resetPassword = useCallback(async (adminId: number, password: string) => {
    await resetAdminPassword(adminId, password);
    // No list change, but re-fetch keeps things consistent if other fields moved.
    await refresh();
  }, [refresh]);

  const resetUsage = useCallback(async (adminId: number, gameType: GameType) => {
    await resetAdminUsage(adminId, gameType);
    await refresh();
  }, [refresh]);

  const setDisabled = useCallback(async (adminId: number, disabled: boolean) => {
    await setAdminDisabled(adminId, disabled);
    await refresh();
  }, [refresh]);

  const removeAdmin = useCallback(async (adminId: number) => {
    await deleteAdmin(adminId);
    await refresh();
  }, [refresh]);

  const loadLoginHistory = useCallback(async (adminId: number) => {
    return getLoginHistory(adminId);
  }, []);

  return {
    admins,
    isLoading,
    error,
    refresh,
    addAdmin,
    resetPassword,
    resetUsage,
    setDisabled,
    removeAdmin,
    loadLoginHistory,
  };
}
